export interface CanvasLayoutFrameInput {
  layoutDirty: boolean;
  bootstrapActive: boolean;
}

export function shouldApplyCanvasLayoutOnFrame(input: CanvasLayoutFrameInput): boolean {
  return input.layoutDirty || input.bootstrapActive;
}

export interface CanvasIntersectionGateInput {
  touchMode: boolean;
  tablet: boolean;
  embedded: boolean;
}

/**
 * Cross-origin iframe intersection observations can report a visible fullscreen
 * canvas as non-intersecting. Page visibility still pauses hidden embedded tabs,
 * so disabling only this second gate preserves the energy-saving behaviour.
 */
export function shouldSkipCanvasIntersectionGate(input: CanvasIntersectionGateInput): boolean {
  return input.touchMode || input.tablet || input.embedded;
}

export interface ViewerHudFrameInput {
  force: boolean;
  worldInteractiveReady: boolean;
  previousWorldInteractiveReady: boolean | undefined;
  debugPerfEnabled: boolean;
  msSinceLastEmit: number;
  intervalMs: number;
}

/**
 * Closed, ready HUDs are event-driven. Loading and debug HUDs use a bounded heartbeat so
 * expensive streaming summaries and React snapshots never run on every animation frame.
 */
export function shouldEmitViewerHud(input: ViewerHudFrameInput): boolean {
  if (input.force) return true;
  if (input.previousWorldInteractiveReady !== input.worldInteractiveReady) return true;
  if (input.worldInteractiveReady && !input.debugPerfEnabled) return false;
  return input.msSinceLastEmit >= input.intervalMs;
}

export interface WorldChromeFrameInput {
  firstUpdate: boolean;
  cameraMoved: boolean;
  shouldRender: boolean;
  overlayAnimating: boolean;
  hoverChanged: boolean;
  visibilityChanged: boolean;
  pointerLockedChanged: boolean;
  placingChanged: boolean;
}

export function shouldUpdateWorldChrome(input: WorldChromeFrameInput): boolean {
  return (
    input.firstUpdate ||
    input.cameraMoved ||
    input.shouldRender ||
    input.overlayAnimating ||
    input.hoverChanged ||
    input.visibilityChanged ||
    input.pointerLockedChanged ||
    input.placingChanged
  );
}

export interface InteractableHoverFrameInput {
  firstUpdate: boolean;
  cameraMoved: boolean;
  visibilityChanged: boolean;
  pointerLockedChanged: boolean;
  placingChanged: boolean;
}

export function shouldUpdateInteractableHover(input: InteractableHoverFrameInput): boolean {
  return (
    input.firstUpdate ||
    input.cameraMoved ||
    input.visibilityChanged ||
    input.pointerLockedChanged ||
    input.placingChanged
  );
}

export interface WorldZonesFrameInput {
  firstUpdate: boolean;
  cameraMoved: boolean;
  visibilityChanged: boolean;
}

/** Zone membership only changes when the camera or world-content visibility changes. */
export function shouldUpdateWorldZones(input: WorldZonesFrameInput): boolean {
  return input.firstUpdate || input.cameraMoved || input.visibilityChanged;
}
